import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { RutFormatGuionDirective } from 'src/app/shared/directives/format-rut-guion/format-rut-guion.directive';

@Component({
  selector: 'app-credentials-step',
  templateUrl: './credentials-step.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    RutFormatGuionDirective,
  ],
})
export class CredentialsStepComponent implements OnDestroy {
  private readonly fb = inject(FormBuilder);

  @Input() isLoading = false;
  @Output() submitForm = new EventEmitter<{ rut: string }>();

  readonly isFocused = signal(false);
  readonly hasError = signal(false);
  readonly errorMessage = signal('');
  readonly attemptsLeft = signal(3);
  readonly isCoolingDown = signal(false);
  readonly cooldownSeconds = signal(0);
  readonly rutInput = viewChild<ElementRef>('rutInput');

  private cooldownInterval?: ReturnType<typeof setInterval>;

  readonly loginForm = this.fb.nonNullable.group({
    rut: ['', [Validators.required, Validators.minLength(9)]],
  });

  get hasValue(): boolean {
    return !!this.loginForm.controls.rut.value;
  }

  get showError(): boolean {
    const control = this.loginForm.controls.rut;
    return control.invalid && (control.dirty || control.touched) && !this.isFocused();
  }

  onSubmit(): void {
    if (this.isCoolingDown()) return;
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.hasError.set(true);
      return;
    }

    const rut = this.loginForm.value.rut!.replace(/[^0-9Kk]/g, '').toUpperCase();

    // Clear previous error when submitting
    this.hasError.set(false);
    this.errorMessage.set('');

    this.submitForm.emit({ rut });
  }

  displayLoginError(message: string, attemptsLeft: number): void {
    this.hasError.set(true);
    this.errorMessage.set(message);
    this.attemptsLeft.set(attemptsLeft);
  }

  startCooldown(): void {
    this.hasError.set(false);
    this.errorMessage.set('');
    this.isCoolingDown.set(true);
    this.cooldownSeconds.set(60);
    this.loginForm.controls.rut.disable();

    this.cooldownInterval = setInterval(() => {
      const remaining = this.cooldownSeconds() - 1;
      if (remaining <= 0) {
        clearInterval(this.cooldownInterval);
        this.isCoolingDown.set(false);
        this.cooldownSeconds.set(0);
        this.loginForm.controls.rut.setValue('');
        this.loginForm.controls.rut.markAsPristine();
        this.loginForm.controls.rut.markAsUntouched();
        this.loginForm.controls.rut.enable();
      } else {
        this.cooldownSeconds.set(remaining);
      }
    }, 1000);
  }

  ngOnDestroy(): void {
    clearInterval(this.cooldownInterval);
  }
}
